import React from 'react'
import cx from 'classnames'

import Icon from 'components/Icon'
import Link from 'components/Link'

import { LinkProps as RRLinkProps } from 'react-router-dom'

import { observer } from 'mobx-react'
import {
  CellProps,
  ColumnDensity,
  DataTestProps,
  DateDisplay,
  ImageCellContent,
  ImageCellProps,
  TextCellContent,
} from '../TableCell'
import styles from './ClickableTable.sass'
import Checkbox from 'components/Checkbox'

interface BaseProps {
  children?: React.ReactNode
  className?: string
}

interface TableProps extends BaseProps {
  striped?: boolean
  stickyHeader?: boolean
}

export function Table({
  children,
  striped,
  stickyHeader,
  className,
}: TableProps) {
  return (
    <div
      className={cx(
        styles.base,
        striped && styles.striped,
        stickyHeader && styles.stickyHeader,
        className,
      )}
    >
      {children}
    </div>
  )
}

interface TableHead extends BaseProps {}

export function TableHead({ children, className }: TableHead) {
  return (
    <div className={cx(styles.base, styles.tableHeader, className)}>
      {children}
    </div>
  )
}

export type TableSortOrder = 'asc' | 'desc' | 'none'

interface TableHeader extends BaseProps {
  /**
   * If a TableHeader `canSort` then it will display a sort inticator and
   * make the header clickable
   */
  canSort?: boolean

  /**
   * If a sort order is specified, a sort order indicator will be displayed
   * Thiw will replace the default hasOrder indicator
   */
  sortOrder?: TableSortOrder

  /**
   * A callback that will be called with the next sortOrder when
   * this table header is clicked
   */
  onSort?: (sortOrder: TableSortOrder) => void
}

export function TableHeader({
  className,
  children,
  canSort = false,
  sortOrder = 'asc',
  onSort = () => null,
  alignment,
}: TableHeader & AlignmentProps) {
  const El = canSort ? 'a' : 'span'
  return (
    <div
      className={cx(
        styles.tableHeaderCell,
        className,
        canSort && styles.sortableHeader,
        alignment && styles[alignment],
      )}
      onClick={() => {
        if (!canSort) {
          return
        }
        switch (sortOrder) {
          case 'none':
            onSort('asc')
            break
          case 'asc':
            onSort('desc')
            break
          default:
            onSort('none')
        }
      }}
    >
      <El
        role={canSort ? 'button' : ''}
        className={cx(canSort && styles.headerWithSort)}
      >
        {children}
        {canSort && (
          <span
            className={cx(
              styles.sortIcon,
              sortOrder === 'asc' && styles.sortIconAsc,
              sortOrder === 'desc' && styles.sortIconDesc,
            )}
          >
            <Icon
              size={14}
              name="sorting"
              rotate={sortOrder !== 'desc' ? -180 : 0}
            />
          </span>
        )}
      </El>
    </div>
  )
}

interface TableBody extends BaseProps {}

export function TableBody({ children, className }: TableBody) {
  return <div className={cx(styles.tableBody, className)}>{children}</div>
}

interface TableRow
  extends BaseProps,
    React.HTMLAttributes<HTMLAnchorElement>,
    Partial<RRLinkProps> {
  active?: boolean
  alwaysShowPointer?: boolean
}

export function TableRow({
  className,
  children,
  active,
  alwaysShowPointer,
  ...rest
}: TableRow) {
  return (
    <Link
      {...rest}
      className={cx(
        styles.tableRow,
        active && styles.active,
        alwaysShowPointer && styles.rowAlwaysShowPointer,
        className,
      )}
    >
      {children}
    </Link>
  )
}

interface TableCell extends BaseProps {}

export function TableCell({ className, children }: TableCell) {
  return <td className={cx(styles.tableCell, className)}>{children}</td>
}

interface AlignmentProps {
  alignment?: 'left' | 'center' | 'right'
}

export const TextCell = observer(function TextCell({
  children,
  alignment,
  dataTest,
}: CellProps & AlignmentProps & DataTestProps) {
  return (
    <TableCell className={cx(styles.textCell, alignment && styles[alignment])}>
      <TextCellContent dataTest={dataTest}>{children}</TextCellContent>
    </TableCell>
  )
})

interface DateCellProps {
  date: Date | null
  density: ColumnDensity
}

export const DateCell = observer(function DateCell({
  date,
  density,
  dataTest,
}: DateCellProps & DataTestProps) {
  return (
    <TextCell dataTest={dataTest}>
      {date && <DateDisplay date={date} density={density} />}
    </TextCell>
  )
})

export const ImageCell = observer(function ImageCell({
  title,
  image,
  imageFallback,
  dataTest,
}: ImageCellProps & DataTestProps) {
  return (
    <TableCell>
      <ImageCellContent
        title={title}
        image={image}
        imageFallback={imageFallback}
        dataTest={dataTest}
      />
    </TableCell>
  )
})

interface CheckboxCellProps {
  checked?: boolean

  onChange?(checked: boolean): unknown
}

export const CheckboxCell = observer(function CheckboxCell({
  checked,
  onChange,
  dataTest,
}: CheckboxCellProps & DataTestProps) {
  return (
    <TableCell>
      <Checkbox
        checked={checked}
        onChange={onChange}
        muteClickEvent
        data-test={dataTest}
      />
    </TableCell>
  )
})

interface CheckCellProps {
  checked?: boolean
  color?: string
}

export const CheckCell = observer(function CheckCell({
  checked,
  color,
  alignment,
}: CheckCellProps & AlignmentProps) {
  return (
    <TableCell>
      <div className={cx(styles.textCenter, alignment && styles[alignment])}>
        {checked ? <Icon name="tick" size={24} color={color} /> : null}
      </div>
    </TableCell>
  )
})
